import type { Store } from 'jotai/vanilla/store'
import { registerSubjectProvider, type Subject, type Transform } from './index'
import { uiAtom } from '@/uiState'
import type { Position } from '@/voxelMath'
import type { EventTuple } from '@/events/types'

const ID_CURRENT = 'current'
const IDENTITY_ROTATION = { x: 0, y: 0, z: 0, w: 1 }

function zeroPosition(): Position {
  return { x: 0, y: 0, z: 0 }
}

function makeSelectionSubject(store: Store): Subject | null {
  const getState = () => store.get(uiAtom)
  const getSelection = () => getState().voxelSelection
  if (!getSelection()) return null

  const subject: Subject = {
    kind: 'selection',
    id: ID_CURRENT,
    center: () => {
      const sel = getSelection()
      if (!sel?.center) return zeroPosition()
      const t = sel.translate || zeroPosition()
      return { x: sel.center.x + (t.x || 0), y: sel.center.y + (t.y || 0), z: sel.center.z + (t.z || 0) }
    },
    bbox: () => {
      const sel = getSelection()
      if (!sel?.boundingBox) return { min: zeroPosition(), max: zeroPosition() }
      const t = sel.translate || zeroPosition()
      return {
        min: { x: sel.boundingBox.min.x + (t.x || 0), y: sel.boundingBox.min.y + (t.y || 0), z: sel.boundingBox.min.z + (t.z || 0) },
        max: { x: sel.boundingBox.max.x + (t.x || 0), y: sel.boundingBox.max.y + (t.y || 0), z: sel.boundingBox.max.z + (t.z || 0) },
      }
    },
    // TODO: Unify selection transform storage with subjectTransforms to mirror builder subjects.
    //       This will enable consistent transform persistence and simplify preview wiring.
    getTransform: (): Transform => {
      const sel = getSelection()
      const translate = sel?.translate ?? zeroPosition()
      return {
        translate: { x: translate.x ?? 0, y: translate.y ?? 0, z: translate.z ?? 0 },
        rotation: { ...IDENTITY_ROTATION },
      }
    },
    setTransform: (transform: Transform) => {
      // TODO: When unifying transform storage, write to ui.subjectTransforms via subjectTransformKey.
      store.set(uiAtom, (prev) => {
        const sel = prev.voxelSelection
        if (!sel) return prev
        return {
          ...prev,
          voxelSelection: {
            ...sel,
            translate: { ...transform.translate },
          }
        }
      })
    },
    clampTransform: (transform) => transform,
    beginInteraction: () => {},
    endInteraction: () => {},
    previewEvents: (): EventTuple[] => [],
    commitTransform: (): EventTuple[] => {
      const sel = getSelection()
      if (!sel) return []
      const t = sel.translate
      const hasMove = !!t && ((t.x ?? 0) !== 0 || (t.y ?? 0) !== 0 || (t.z ?? 0) !== 0)
      return hasMove ? [['doc.selection.commit']] : []
    }
  }

  return subject
}

registerSubjectProvider('selection', (id, store) => {
  if (id !== ID_CURRENT) return null
  return makeSelectionSubject(store)
})

export const selectionSubjectId = ID_CURRENT
